import React, {useEffect, useState} from "react";
import {userSyncService} from "../../../services/user-sync.service";
import {FullPageLoader} from "../../../utils/FullPageLoader";
import {IPaginationInfo} from "../../../interfaces/pagination-info.interface";
import moment from "moment/moment";
import {Table} from 'antd';
import {ColumnsType} from "antd/es/table";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getUser} from "../../auth/slice/user.selector";
import {useColumnSearch} from "./getColumnSearchProps";
import {ProcessUserSyncFileModal} from "./ProcessUserSyncFileModal";
import {DeleteUserSyncCSVModal} from "./DeleteUserSyncCSVModal";
import {randomId} from "../../../utils/randomId";
import {EAssignmentKeyStatus, EUserSyncAction} from "../../../interfaces/user-sync.enums";

interface MyItem {
    csvFileName: string;
    action: EUserSyncAction;
    key: string;
    organizationMembers: any[];
    actionStatus: string;
    assignmentKeyStatus: EAssignmentKeyStatus;
    createdAt: string;
    organization: string;
}

export const UserSyncTab: React.FC<any> = (props) => {

    const [processedData, setProcessedData] = useState<any>([]);
    const [processedDataPagination, setProcessedDataPagination] = useState<IPaginationInfo>()
    const [processUserSyncFileToggle, setProcessUserSyncFileToggle] = useState(false);
    const [deleteUserSyncCsvModal, setDeleteUserSyncCsvModal] = useState<boolean>(false)
    const [selectedFile, setSelectedFile] = useState<string>('Show All');

    const [fileNames, setFileNames] = useState<any[]>([])

    const [fullLoader, setFullLoader] = useState(false);
    const {t} = useTranslation('', {keyPrefix: 'UserSyncTab'});
    const currentUser = useSelector(getUser);
    const {getColumnSearchProps} = useColumnSearch();

    useEffect(() => {
        setFullLoader(true);
        getImportedCodes(1)
        getOrganizationCsvFiles()
    }, []);

    const getImportedCodes = async (page: number = 1, accumulatedData: any[] = [], fileName?: string) => {
        // if (page === 1 && fileName) {
        //     setProcessedData([]); // Clear table before reloading with new file
        // }
        const res: any = await userSyncService.getOrgProcessedData(props.id, page, fileName);
        if (res.statusCode === 201) {
            const updatedData = page == 1 ? [...res.payload.codes.records] : [...accumulatedData, ...res.payload.codes.records];
            setProcessedDataPagination(res.payload.codes.paginationInfo);
            setProcessedData([...updatedData]);
            const pagination = res.payload.codes.paginationInfo;
            setFullLoader(false);
            if (updatedData.length < pagination.totalRecords) {
                await getImportedCodes(pagination.currentPage + 1, updatedData);
            }
        }
    };

    const openProfile = (user: any) => {
        if (currentUser._id === user._id) {
            window.open("/profile-page", "_blank");
        } else {
            window.open(`/memberprofilepage/${user._id}`, "_blank");
        }
    }
    const getOrganizationCsvFiles = async () => {
        const res: any = await userSyncService.getOrgProcessedCsvFiles(props.id)
        if (res.payload.fileNames.length > 0) {
            const temp = ['Show All', ...res.payload.fileNames];
            setFileNames(temp);
            setSelectedFile((prev) => (prev ? prev : 'Show All')); // keep if already selected, else default
            setFullLoader(false);
        }
    }
    const columns: ColumnsType<MyItem> = [
        {
            title: (t('key')).toString(),
            dataIndex: 'key',
            key: 'key',
            sorter: (a, b) => a.key.localeCompare(b.key),
            ...getColumnSearchProps('key', 'Key'),
        },
        {
            title: (t('action')).toString(),
            dataIndex: 'action',
            key: 'action',
            ...getColumnSearchProps('action', 'Action'),
            render: (text) => text.replaceAll('_', ' '),
            sorter: (a, b) => a.action.localeCompare(b.action),
        },
        {
            title: (t('match')).toString(),
            dataIndex: 'organizationMembers',
            key: 'organizationMembers',
            ...getColumnSearchProps('organizationMembers', 'Organization Members'),
            render: (members: any[], masterObject) =>
                members && members.length > 0 ? (
                    members.map((m) => (
                        <div key={m._id}>
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => openProfile(m.member)}
                        >
                            {m.member.name || 'No name'}
                        </span>
                        </div>
                    ))
                ) : (
                    <span>{getAssignmentKeyMessage(masterObject.assignmentKeyStatus, masterObject)}</span>
                ),
            sorter: (a, b) => {
                const aName = a.organizationMembers?.[0]?.name || '';
                const bName = b.organizationMembers?.[0]?.name || '';
                return aName.localeCompare(bName);
            },
        },
        {
            title: (t('actionSuccess')).toString(),
            dataIndex: 'actionStatus',
            key: 'actionStatus',
            sorter: (a, b) => a.actionStatus.localeCompare(b.actionStatus),
            ...getColumnSearchProps('actionStatus', 'Action Success'), // ✅ fixed
        },
        {
            title: (t('timestamp')).toString(),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => moment(text).format('MMMM Do YYYY, h:mm:ss a'),
            sorter: (a, b) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        },
    ];

    const getAssignmentKeyMessage = (input: EAssignmentKeyStatus, masterObject: MyItem): string => {
        if (masterObject.action == EUserSyncAction.REMOVE && masterObject.actionStatus === 'Success') {
            return 'User Removed'
        } else {
            switch (input) {
                case EAssignmentKeyStatus.USED:
                    return 'Assignment key already used’'
                case EAssignmentKeyStatus.ACTIVE:
                    return 'Assignment key already exists and active, please check'
                case EAssignmentKeyStatus.NOT_EXIST:
                    return 'New Assignment Key Added'
                case EAssignmentKeyStatus.REACTIVATED:
                    return 'Existing assignment key has been reactivated'
                default:
                    return 'No Match found'
            }
        }
    }
    const openUserSyncDeleteCsvModal = () => {
        setDeleteUserSyncCsvModal(true)
    }
    const closeDeleteUserSyncCsvModal = (data: boolean) => {
        setDeleteUserSyncCsvModal(false)
    };
    return <div>
        {fullLoader && <FullPageLoader/>}
        <div className="up-membersarea">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {fileNames.length > 0 ? <>
                    <div className="form-group" style={{float: 'right', width: 240}}>
                        <label>{t('selectFile')}</label>
                        <select
                            className="form-control"
                            value={selectedFile}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setSelectedFile(selected);
                                setFullLoader(true);
                                if (selected === 'Show All') {
                                    getImportedCodes(1);
                                } else {
                                    getImportedCodes(1, [], selected);
                                }
                            }}
                        >
                            {fileNames.map((e, key) => (
                                <option key={key + e} value={e}>
                                    {e}
                                </option>
                            ))}
                        </select>
                    </div>
                </> : <div></div>}
                <div>
                    <button className="up-btninvitemembers up-btn"
                            style={{borderRadius: 12, height: 48, marginRight: 18, marginBottom: 25}}
                            onClick={() => setProcessUserSyncFileToggle(true)}
                            type="button">{t("processNewFile")}</button>
                    {/*<button className="up-btninvitemembers up-btn" style={{ borderRadius: 12 ,height: 48}}*/}
                    {/*        onClick={() => openUserSyncDeleteCsvModal()}>{t("deleteCSV")}</button>*/}
                </div>
            </div>
            {processedData?.length > 0 && <div className="up-themebox">
                <Table
                    columns={columns}
                    dataSource={processedData}
                    rowKey={(record: any) => record.organization + record.csvFileName + randomId()}
                    pagination={false}
                    showSorterTooltip={false}
                />
                <p className="up-allpostseen" style={{textAlign: 'center'}}>
                    <b>{t('seenItAll')}</b>
                </p>
            </div>}
        </div>
        {processedData?.length == 0 && <div className="up-themebox">
            <h5>{t('noKeysImportedYet')}</h5></div>}

        {processUserSyncFileToggle && <ProcessUserSyncFileModal id={props.id} closeModal={(data: boolean) => {
            setProcessUserSyncFileToggle(false)
            if (data) {
                setFullLoader(true)
                getOrganizationCsvFiles()
                getImportedCodes(1)
                setSelectedFile('Show All');
            }
        }}/>}
        {/* Delete User Sync CSV Modal */}
        {deleteUserSyncCsvModal && <DeleteUserSyncCSVModal closeModal={closeDeleteUserSyncCsvModal} id={props.id}/>}

    </div>
}
